import React from "react";
import { FONT } from "../constants";
import { C } from "../styles";
import PayTypeKebabMenu from "./PayTypeKebabMenu";

interface Props {
  category: "earning" | "deduction";
  items: any[];
  kebabOpenId: string | number | null;
  onRowClick: (item: any) => void;
  onKebabToggle: (id: string | number, e: React.MouseEvent) => void;
  onEdit: (item: any) => void;
  onDuplicate: (item: any) => void;
  onToggleActive: (item: any) => void;
  onDelete: (item: any) => void;
}

function formatCalc(item: any, isDeduction: boolean) {
  const method = item.calc_method;
  const rate = isDeduction ? item.default_amount : item.default_rate;
  const unit = item.unit_label || "";
  if (method === "fixed") return rate != null ? { main: Number(rate).toFixed(2), suffix: unit } : { main: "Fixed", suffix: "" };
  if (method === "rate_hours") return rate != null ? { main: Number(rate).toFixed(2), suffix: unit || "per hour" } : { main: "Rate", suffix: "x hours" };
  if (method === "rate_units") return rate != null ? { main: Number(rate).toFixed(2), suffix: unit } : { main: "Rate", suffix: "x units" };
  if (method === "percent_gross") return rate != null ? { main: Number(rate).toFixed(2), suffix: "% of gross" } : { main: "%", suffix: "of gross" };
  return { main: "Variable", suffix: "" };
}

function formatTaxTreatment(pt: any) {
  const flags: string[] = [];
  if (pt.federal_taxable) flags.push("Federal");
  if (pt.cpp_contributable) flags.push("CPP");
  if (pt.ei_insurable) flags.push("EI");
  if (pt.vacationable) flags.push("Vacation");
  if (flags.length === 0) return { primary: "Non-taxable reimbursement", secondary: null as string | null, nonTax: true };
  if (pt.federal_taxable && pt.cpp_contributable && pt.ei_insurable && pt.vacationable) return { primary: "Fully taxable", secondary: "Federal, CPP, EI, Vacation", nonTax: false };
  if (pt.federal_taxable && pt.cpp_contributable && pt.ei_insurable && !pt.vacationable) return { primary: "Taxable, non-vacationable", secondary: "Federal, CPP, EI", nonTax: false };
  return { primary: "Partially taxable", secondary: flags.join(", "), nonTax: false };
}

function formatDeductionTreatment(d: any) {
  const parts: string[] = [];
  if (d.is_pre_tax) parts.push("Pre-tax"); else parts.push("Post-tax");
  if (d.employer_matched) parts.push(d.calc_method === "fixed" ? "employer shared" : "employer matched");
  return { primary: parts.join(", "), secondary: d.is_pre_tax ? "Reduces federal taxable income" : null as string | null };
}

export default function PayTypesTable({
  category, items, kebabOpenId,
  onRowClick, onKebabToggle, onEdit, onDuplicate, onToggleActive, onDelete,
}: Props) {
  const isEarning = category === "earning";
  const accentDot = isEarning ? "#0D8050" : "#9C5A0F";

  return (
    <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 10, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1.2fr 56px", gap: 18, padding: "13px 22px", background: "#F4F6F8", borderBottom: "1px solid " + C.line, fontSize: 11, fontWeight: 700, color: C.ink, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        <div>Name</div>
        <div>Calculation</div>
        <div>Tax treatment</div>
        <div></div>
      </div>

      {items.map((item: any, idx: number) => {
        const calc = formatCalc(item, !isEarning);
        const tax = isEarning ? formatTaxTreatment(item) : { ...formatDeductionTreatment(item), nonTax: false };
        const flipUp = idx >= items.length - 2;
        const dotColor = isEarning ? ((tax as any).nonTax ? (C as any).faint : accentDot) : accentDot;

        return (
          <div
            key={item.id}
            onClick={() => onRowClick(item)}
            style={{ display: "grid", gridTemplateColumns: "2fr 1.4fr 1.2fr 56px", gap: 18, padding: "16px 22px", borderBottom: "1px solid " + (C as any).lineSoft, alignItems: "center", cursor: "pointer" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: dotColor, flex: "0 0 8px" }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, marginBottom: 2, letterSpacing: "-0.005em" }}>{item.name}</div>
                <div style={{ fontSize: 12, color: isEarning ? C.ink : (C as any).muted, fontWeight: isEarning ? 500 : 400 }}>{item.description || (item.is_default ? "" : "Custom")}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.ink, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: "tabular-nums" }}>
              {calc.main}{calc.suffix && <span style={{ color: (C as any).faint, fontWeight: 400, fontFamily: FONT, marginLeft: 2, fontSize: 11.5 }}> {calc.suffix}</span>}
            </div>
            <div style={{ fontSize: 12.5, color: (C as any).text }}>
              <span style={{ fontWeight: 500, color: (tax as any).nonTax ? (C as any).muted : C.ink, fontStyle: (tax as any).nonTax ? "italic" : "normal" }}>{tax.primary}</span>
              {tax.secondary && <span style={{ color: (C as any).muted, fontSize: 11.5, marginTop: 2, display: "block" }}>{tax.secondary}</span>}
            </div>
            <PayTypeKebabMenu
              isOpen={kebabOpenId === item.id}
              isDefault={!!item.is_default}
              isActive={!!item.is_active}
              flipUp={flipUp}
              onToggle={(e) => onKebabToggle(item.id, e)}
              onEdit={() => onEdit(item)}
              onDuplicate={() => onDuplicate(item)}
              onToggleActive={() => onToggleActive(item)}
              onDelete={() => onDelete(item)}
            />
          </div>
        );
      })}
    </div>
  );
}